/**
 * clip_session.ts
 *
 * Implementation of {@link VgpsClipSession} — clip-download session that
 * receives a recorded fMP4 over a WebRTC DataChannel. Originally lived
 * inline in `vgps_cms_fe/src/app/sdk/livekit/hooks/usePlaybackChannel.js`;
 * this file owns the SDK-side wire contract so apps never construct
 * RTCPeerConnection / WebSocket / MediaSource themselves.
 *
 * Wire flow (matches signaling-server-python handle_sdk_message):
 *   viewer →  session_start    {session_id, device_serial, channel_id}
 *   device →  sdp_offer        {session_id, sdp}
 *   viewer →  sdp_answer       {session_id, sdp}
 *   device →  ice_candidate    {session_id, candidate, sdp_mid, sdp_mline_index}
 *   viewer →  playback_request {device_serial, slot_id, camera, session_id}
 *
 * Then on the DataChannel labelled "playback":
 *   device →  TEXT  playback_begin  {size, slot_id, duration_ms}
 *   device →  BINARY fMP4 fragments  (ftyp+moov init, then moof+mdat)
 *   device →  TEXT  playback_end
 *   device →  TEXT  playback_error  {message}
 *
 * If MSE can decode the codec (HEVC hvc1/hev1 or AVC avc1), the session
 * streams progressively into a MediaSource SourceBuffer and the consumer
 * can press Play before the download finishes. If MSE can't (e.g. HEVC on
 * Firefox), the session accumulates the bytes and assembles a Blob URL at
 * `playback_end`.
 */

import type {
  ClipSessionRequest,
  ClipSessionStatus,
  IceServerConfig,
  VgpsClipSession,
  VgpsClipSessionState,
} from '../runtime/types';
import type { Unsubscribe } from '../events/event_bus';

// MediaSource codec candidates for HEVC/AVC fMP4, most-specific first.
const MSE_CODEC_CANDIDATES = [
  'video/mp4; codecs="hvc1.1.6.L93.B0"',
  'video/mp4; codecs="hev1.1.6.L93.B0"',
  'video/mp4; codecs="hvc1.1.6.L120.B0"',
  'video/mp4; codecs="hvc1.2.4.L120.B0"',
  'video/mp4; codecs="hvc1"',
  'video/mp4; codecs="avc1.64002a"',
  'video/mp4; codecs="avc1.64001f"',
  'video/mp4; codecs="avc1.4d401f"',
  'video/mp4; codecs="avc1.42e01f"',
  'video/mp4; codecs="avc1"',
];

function pickMseCodec(): string | null {
  if (typeof window === 'undefined' || !window.MediaSource) return null;
  for (const c of MSE_CODEC_CANDIDATES) {
    try { if (window.MediaSource.isTypeSupported(c)) return c; } catch { /* ignore */ }
  }
  return null;
}

interface TransferState {
  mode: 'mse' | 'blob';
  size: number;
  received: number;
  mediaSource?: MediaSource;
  sourceBuffer?: SourceBuffer;
  queue: ArrayBuffer[];
  ended: boolean;
  blobChunks: ArrayBuffer[];
}

interface ClipSessionDeps {
  signalingUrl: string;
  iceServers: IceServerConfig[];
}

const DEFAULT_ICE_SERVERS: IceServerConfig[] = [];

export class ClipSession implements VgpsClipSession {
  readonly sessionId: string;
  private readonly req: ClipSessionRequest;
  private readonly deps: ClipSessionDeps;

  private ws: WebSocket | null = null;
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private currentObjectUrl: string | null = null;
  private xfer: TransferState | null = null;
  private closed = false;

  private state: VgpsClipSessionState = {
    status: 'connecting',
    error: null,
    videoUrl: null,
    progress: 0,
    durationMs: 0,
    slotId: null,
    buffering: false,
  };

  private readonly listeners = new Set<(state: VgpsClipSessionState) => void>();

  constructor(sessionId: string, req: ClipSessionRequest, deps: ClipSessionDeps) {
    this.sessionId = sessionId;
    this.req = req;
    this.deps = deps;
  }

  getState(): VgpsClipSessionState {
    return this.state;
  }

  subscribeState(listener: (state: VgpsClipSessionState) => void): Unsubscribe {
    this.listeners.add(listener);
    // Fire once with the current state so subscribers don't miss the initial value.
    try { listener(this.state); } catch { /* isolate */ }
    return () => this.listeners.delete(listener);
  }

  /**
   * Open the WS + wait for the device's sdp_offer to be processed.
   * Resolves once the DataChannel side is hooked up (status becomes 'ready'
   * when the channel opens).
   */
  async start(): Promise<void> {
    if (this.closed) throw new Error('ClipSession.start() on closed session');
    if (this.ws) return; // idempotent
    const url = `${this.deps.signalingUrl.replace(/\/$/, '')}/${this.sessionId}`;
    const ws = new WebSocket(url);
    this.ws = ws;
    this.update({ status: 'connecting', error: null });

    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: 'session_start',
        session_id: this.sessionId,
        device_serial: this.req.deviceSerial,
        channel_id: this.req.channelId,
      }));
    };

    ws.onmessage = (e) => this.handleSignalingMessage(e);

    ws.onerror = () => {
      this.update({ status: 'error', error: 'signaling WebSocket error' });
    };

    ws.onclose = () => {
      // If we get a close before reaching 'ready', surface as an error.
      if (!this.closed && (this.state.status === 'connecting' || this.state.status === 'ready')) {
        this.update({ status: 'error', error: 'signaling WebSocket closed unexpectedly' });
      }
    };
  }

  requestPlayback(slotId: number, camera: number = 1): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      this.update({ error: 'playback channel not connected' });
      return;
    }
    // Reset transfer state for the new clip.
    this.disposeCurrentTransfer();
    this.update({
      videoUrl: null,
      error: null,
      progress: 0,
      slotId,
    });
    ws.send(JSON.stringify({
      type: 'playback_request',
      device_serial: this.req.deviceSerial,
      slot_id: slotId,
      camera,
      session_id: this.sessionId,
    }));
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    try { this.ws?.close(); } catch { /* ignore */ }
    this.ws = null;
    try { this.pc?.close(); } catch { /* ignore */ }
    this.pc = null;
    this.dc = null;
    this.disposeCurrentTransfer();
    if (this.currentObjectUrl) {
      try { URL.revokeObjectURL(this.currentObjectUrl); } catch { /* ignore */ }
      this.currentObjectUrl = null;
    }
    this.listeners.clear();
  }

  // ── Internals ───────────────────────────────────────────────────────────

  private update(patch: Partial<VgpsClipSessionState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) {
      try { l(this.state); } catch { /* isolate */ }
    }
  }

  private async handleSignalingMessage(e: MessageEvent): Promise<void> {
    let msg: { type?: string; sdp?: string; candidate?: string; sdp_mid?: string;
               sdp_mline_index?: number; status?: string; message?: string };
    try { msg = JSON.parse(e.data); } catch { return; }

    if (msg.type === 'sdp_offer' && msg.sdp) {
      try {
        const pc = new RTCPeerConnection({
          iceServers: (this.req.iceServers ?? this.deps.iceServers ?? DEFAULT_ICE_SERVERS) as RTCIceServer[],
        });
        this.pc = pc;
        pc.ondatachannel = (ev) => {
          if (ev.channel.label === 'playback') this.setupDataChannel(ev.channel);
        };
        pc.onconnectionstatechange = () => {
          if (pc.connectionState === 'failed') {
            this.update({ status: 'error', error: 'WebRTC connection failed' });
          }
        };
        await pc.setRemoteDescription({ type: 'offer', sdp: msg.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await this.waitIceComplete(pc);
        const ws = this.ws;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({
          type: 'sdp_answer',
          session_id: this.sessionId,
          sdp: pc.localDescription?.sdp ?? '',
        }));
      } catch (err) {
        this.update({
          status: 'error',
          error: `WebRTC negotiation failed: ${err instanceof Error ? err.message : String(err)}`,
        });
      }
      return;
    }

    if (msg.type === 'ice_candidate' && this.pc && msg.candidate !== undefined) {
      try {
        await this.pc.addIceCandidate({
          candidate: msg.candidate,
          sdpMid: msg.sdp_mid,
          sdpMLineIndex: msg.sdp_mline_index,
        });
      } catch { /* ignore — trickle ICE failures are non-fatal */ }
      return;
    }

    if (msg.type === 'playback_ack' && msg.status === 'error') {
      this.update({
        status: 'error',
        error: msg.message ?? 'device rejected playback request',
        buffering: false,
      });
    }
  }

  private setupDataChannel(dc: RTCDataChannel): void {
    this.dc = dc;
    dc.binaryType = 'arraybuffer';
    dc.onopen = () => {
      this.update({ status: 'ready' });
    };
    dc.onclose = () => {
      // No-op — closing is expected at end-of-clip.
    };
    dc.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        let m: { type?: string; size?: number; slot_id?: number;
                 duration_ms?: number; message?: string };
        try { m = JSON.parse(ev.data); } catch { return; }
        if (m.type === 'playback_begin') {
          this.beginTransfer({ size: m.size ?? 0, slot_id: m.slot_id, duration_ms: m.duration_ms ?? 0 });
        } else if (m.type === 'playback_end') {
          this.endTransfer();
        } else if (m.type === 'playback_error') {
          this.update({
            status: 'error',
            error: m.message ?? 'device playback error',
            buffering: false,
          });
          this.xfer = null;
        }
      } else {
        this.onChunk(ev.data as ArrayBuffer);
      }
    };
  }

  private beginTransfer(meta: { size: number; slot_id?: number; duration_ms: number }): void {
    // Revoke any prior object URL.
    if (this.currentObjectUrl) {
      try { URL.revokeObjectURL(this.currentObjectUrl); } catch { /* ignore */ }
      this.currentObjectUrl = null;
    }
    this.update({
      slotId: meta.slot_id ?? null,
      durationMs: meta.duration_ms,
      progress: 0,
      status: 'transferring',
      buffering: true,
    });

    const codec = pickMseCodec();
    if (codec) {
      const ms = new MediaSource();
      this.xfer = {
        mode: 'mse', size: meta.size, received: 0,
        mediaSource: ms, sourceBuffer: undefined, queue: [], ended: false, blobChunks: [],
      };
      ms.addEventListener('sourceopen', () => {
        const x = this.xfer;
        if (!x || x.mode !== 'mse') return;
        try {
          const sb = ms.addSourceBuffer(codec);
          sb.mode = 'sequence';
          x.sourceBuffer = sb;
          sb.addEventListener('updateend', () => this.pumpMse());
          this.pumpMse();
        } catch (err) {
          this.update({
            error: `trình duyệt không hỗ trợ giải mã clip (MSE/${codec.split('"')[1]})`,
          });
        }
      });
      this.currentObjectUrl = URL.createObjectURL(ms);
      this.update({ videoUrl: this.currentObjectUrl });
    } else {
      this.xfer = {
        mode: 'blob', size: meta.size, received: 0,
        queue: [], ended: false, blobChunks: [],
      };
    }
  }

  private onChunk(buf: ArrayBuffer): void {
    const x = this.xfer;
    if (!x) return;
    x.received += buf.byteLength;
    const progress = x.size > 0 ? x.received / x.size : 0;
    this.update({ progress });
    if (x.mode === 'mse') {
      x.queue.push(buf);
      this.pumpMse();
    } else {
      x.blobChunks.push(buf);
    }
  }

  private endTransfer(): void {
    const x = this.xfer;
    if (!x) return;
    this.update({ buffering: false, progress: 1, status: 'completed' });
    if (x.mode === 'mse') {
      x.ended = true;
      this.pumpMse(); // flush remaining + endOfStream
    } else {
      const blob = new Blob(x.blobChunks, { type: 'video/mp4' });
      if (this.currentObjectUrl) {
        try { URL.revokeObjectURL(this.currentObjectUrl); } catch { /* ignore */ }
      }
      this.currentObjectUrl = URL.createObjectURL(blob);
      this.update({ videoUrl: this.currentObjectUrl });
      this.xfer = null;
    }
  }

  private pumpMse(): void {
    const x = this.xfer;
    if (!x || x.mode !== 'mse' || !x.sourceBuffer || !x.mediaSource) return;
    if (x.sourceBuffer.updating) return;
    if (x.queue.length > 0) {
      const next = x.queue.shift()!;
      try {
        x.sourceBuffer.appendBuffer(next);
      } catch (err) {
        // QuotaExceeded or invalid bytes — surface to consumer.
        this.update({ error: 'lỗi giải mã MSE (đã từ chối fragment)' });
      }
    } else if (x.ended && x.mediaSource.readyState === 'open') {
      try { x.mediaSource.endOfStream(); } catch { /* ignore */ }
    }
  }

  private disposeCurrentTransfer(): void {
    const x = this.xfer;
    if (!x) return;
    if (x.mode === 'mse' && x.mediaSource) {
      try {
        if (x.mediaSource.readyState === 'open') x.mediaSource.endOfStream();
      } catch { /* ignore */ }
    }
    this.xfer = null;
  }

  private waitIceComplete(pc: RTCPeerConnection): Promise<void> {
    return new Promise<void>((resolve) => {
      if (pc.iceGatheringState === 'complete') return resolve();
      let resolved = false;
      const settle = () => { if (!resolved) { resolved = true; resolve(); } };
      const onChange = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', onChange);
          settle();
        }
      };
      pc.addEventListener('icegatheringstatechange', onChange);
      // Hard timeout — avoid stalling forever if the gathering state machine glitches.
      // 4s gives plenty of time for STUN candidates; falls back to whatever we have.
      setTimeout(() => {
        pc.removeEventListener('icegatheringstatechange', onChange);
        settle();
      }, 4_000);
    });
  }
}
